(ns common.lp-portal-import-errors
  "Shape and copy for LP report import formula errors, localized at display time."
  (:require
   [clojure.string :as string]
   [common.localization :as loc]))

(def max-listed-groups
  "How many groups a message names before summarizing the rest."
  5)

(defn total-error-count
  "Cells affected across all groups, not the number of groups."
  [formula-errors]
  (reduce + 0 (map (fn [group]
                     (or (:excel/count group) 1))
                   formula-errors)))

(defn group-description
  "One line for a sheet/function group: how many cells, and a few examples."
  [group]
  (let [{:excel/keys [sheet function]} group
        ;; Older stored attempts hold a single :excel/cell rather than the
        ;; :excel/cells sample a group carries.
        cells (or (seq (:excel/cells group))
                  (some-> (:excel/cell group) vector))
        cell-count (or (:excel/count group) 1)
        examples (string/join ", " cells)]
    (cond
      (and (string/blank? function) (= 1 cell-count))
      (loc/tr :lp-import-error-group-cell-one [sheet (first cells)])

      (string/blank? function)
      (loc/tr :lp-import-error-group-cells [sheet cell-count examples])

      (= 1 cell-count)
      (loc/tr :lp-import-error-formula-cell-function [sheet (first cells) function])

      :else
      (loc/tr :lp-import-error-group [sheet cell-count function examples]))))

(defn excel-error-message
  "Copy for the failures `backend.domain.files.excel` reports.

  That namespace is shared with data-import, export and survey, so it names
  what went wrong rather than what to tell an LP user; the wording belongs
  here, where both the sync and async import paths can reach it."
  [data]
  (case (:excel/error data)
    :excel.error/sheet-not-found
    (loc/tr :lp-import-error-sheet-not-found [(:excel/sheet-name data)])

    nil))

(defn formula-errors-message
  "Localized message: total, grouped locations, limitation. nil when empty."
  [formula-errors]
  (when (seq formula-errors)
    (let [total (total-error-count formula-errors)
          listed (take max-listed-groups formula-errors)
          remaining (- (count formula-errors) (count listed))
          lines (cond-> (mapv group-description listed)
                  (pos? remaining)
                  (conj (loc/tr :lp-import-error-more-groups [remaining])))]
      (str (if (= 1 total)
             (loc/tr :lp-import-error-formula-intro-one)
             (loc/tr :lp-import-error-formula-intro [total]))
           " "
           (string/join "; " lines)
           ". "
           ;; An unsupported function can be named; a formula POI could not
           ;; even parse cannot, so do not claim it is a function problem.
           (if (every? (fn [group]
                         (string/blank? (:excel/function group)))
                       formula-errors)
             (loc/tr :lp-import-error-formula-advice-unevaluatable)
             (loc/tr :lp-import-error-formula-advice))))))

(defn attempt-detail
  "Localized detail for one past import attempt. Handles every shape the stored
  status has taken: grouped errors, the earlier per-cell errors, a raw message
  from before either existed, and attempts that recorded no detail at all."
  [{:keys [formula-errors message outcome]}]
  (cond
    (seq formula-errors) (formula-errors-message formula-errors)
    (not (string/blank? message)) message
    (= :processing.status/error outcome) (loc/tr :parsing-error)))
